import { createRoot } from 'react-dom/client';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';

import App from './app/App';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { SelectedLanguageProvider } from './hooks/selectedLanugage';
import { QAWebToolProvider } from './hooks/useQAWebTool';

import './index.scss';

const getLocationEnv = () => {
    const host = window.location.href;
    if (host.includes('dev-growth')) {
        return 'dev-growth';
    } else if (host.includes('dev')) {
        return 'dev';
    } else if (host.includes('staging')) {
        return 'staging';
    } else if (host.includes('tocaboca.com')) {
        return 'prod';
    } else {
        return 'local';
    }
};

const env = getLocationEnv();
if (env !== 'local') {
    datadogRum.init({
        applicationId: '11f88ad5-5446-47c3-934f-65c0fcf68280',
        clientToken: 'pube7e1f2e0a342aeb65f8820880c15929c',
        site: 'datadoghq.eu',
        service: 'cms',
        env: env,
        version: import.meta.env.VITE_DD_COMMIT_SHA ?? 'local',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        traceSampleRate: 100,
        defaultPrivacyLevel: 'mask-user-input',
        plugins: [reactPlugin({ router: true })],
        allowedTracingUrls: [
            'https://lifeserver-admin-dev-growth.tocaboca.com',
            'https://lifeserver-admin-dev.tocaboca.com',
            'https://lifeserver-admin-staging.tocaboca.com',
            'https://lifeserver-admin.tocaboca.com',
        ],
    });
}

const queryClient = new QueryClient();

const theme = createTheme({
    palette: {
        primary: {
            main: '#11b0b2',
            contrastText: '#fff',
        },
        secondary: {
            main: '#ebf3f2',
            contrastText: '#fff',
        },
    },
});

const container = document.getElementById('root') as HTMLDivElement;
const root = createRoot(container);
root.render(
    <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <QueryClientProvider client={queryClient}>
                <SelectedLanguageProvider>
                    <QAWebToolProvider>
                        <App />
                    </QAWebToolProvider>
                </SelectedLanguageProvider>
            </QueryClientProvider>
        </LocalizationProvider>
    </ThemeProvider>,
);

// Axios send credentials cross site
axios.defaults.withCredentials = true;
